import { z } from "zod";

// ============================================
// SCHEMAS DE VALIDACIÓN PARA CADA TIPO DE GRÁFICO
// ============================================

// BAR CHART
export const BarChartDataItemSchema = z.object({
  label: z.string().min(1, "La etiqueta no puede estar vacía"),
  value: z.number().min(0, "El valor debe ser mayor o igual a 0"),
  color: z.string().optional(),
});

export const BarChartOptionsSchema = z.object({
  barColor: z.string().optional(),
  barBackgroundColor: z.string().optional(),
  "color-text": z.string().optional(),
});

export const BarChartRequestSchema = z.object({
  type: z.literal("bar"),
  data: z.array(BarChartDataItemSchema).min(1, "Se requiere al menos un elemento"),
  options: BarChartOptionsSchema.optional(),
});

// PIE CHART
export const PieChartDataItemSchema = z.object({
  label: z.string().min(1, "La etiqueta no puede estar vacía"),
  value: z.number().min(0, "El valor debe ser mayor o igual a 0").max(10, "El valor debe ser menor o igual a 10"),
  color: z.string().min(1, "Se requiere un color"),
  "color-text": z.string().optional(),
});

export const PieChartRequestSchema = z.object({
  type: z.literal("pie"),
  data: z.array(PieChartDataItemSchema).length(3, "El gráfico pie requiere exactamente 3 elementos"),
});

// RADIAL CHART
export const RadialDimensionSchema = z.object({
  name: z.string().min(1, "El nombre de la dimensión no puede estar vacío"),
  barValue: z.number().min(0, "El barValue debe ser mayor o igual a 0").max(100, "El barValue debe ser menor o igual a 100"),
  lineValue: z.number().min(0, "El lineValue debe ser mayor o igual a 0").max(100, "El lineValue debe ser menor o igual a 100"),
});

export const RadialCategorySchema = z.object({
  name: z.string().min(1, "El nombre de la categoría no puede estar vacío"),
  color: z.string().min(1, "Se requiere un color"),
  dimensions: z.array(RadialDimensionSchema).min(1, "Se requiere al menos una dimensión por categoría"),
});

export const RadialChartRequestSchema = z.object({
  type: z.literal("radial"),
  data: z.array(RadialCategorySchema).min(1, "Se requiere al menos una categoría"),
});

// BAR-REFERRAL CHART
export const BarReferralDataItemSchema = z.object({
  label: z.string().min(1, "La etiqueta no puede estar vacía"),
  userValue: z.number().min(0, "El userValue debe ser mayor o igual a 0").max(10, "El userValue debe ser menor o igual a 10"),
  referralValue: z.number().min(0, "El referralValue debe ser mayor o igual a 0").max(10, "El referralValue debe ser menor o igual a 10"),
});

export const BarReferralOptionsSchema = z.object({
  userColor: z.string().min(1, "Se requiere un color para usuario"),
  referralColor: z.string().min(1, "Se requiere un color para referido"),
  userLabel: z.string().min(1, "Se requiere una etiqueta para usuario"),
  referralLabel: z.string().min(1, "Se requiere una etiqueta para referido"),
});

export const BarReferralChartRequestSchema = z.object({
  type: z.literal("bar-referral"),
  data: z.array(BarReferralDataItemSchema).min(1, "Se requiere al menos un elemento"),
  options: BarReferralOptionsSchema,
});

// PIE-ONLY-COLOR CHART
export const PieOnlyColorDataItemSchema = z.object({
  label: z.string().min(1, "La etiqueta no puede estar vacía"),
  value: z.number().min(0, "El valor debe ser mayor o igual a 0").max(10, "El valor debe ser menor o igual a 10"),
  color: z.string().optional(),
  "color-text": z.string().optional(),
});

export const PieOnlyColorChartRequestSchema = z.object({
  type: z.literal("pie-only-color"),
  data: z.array(PieOnlyColorDataItemSchema).length(3, "El gráfico pie-only-color requiere exactamente 3 elementos"),
});

// PIE-UPDATE CHART
export const PieUpdateDataItemSchema = z.object({
  label: z.string().min(1, "La etiqueta no puede estar vacía"),
  value: z.number().min(0, "El valor debe ser mayor o igual a 0"),
  color: z.string().optional(),
});

export const PieUpdateChartRequestSchema = z.object({
  type: z.literal("pie-update"),
  data: z.array(PieUpdateDataItemSchema).length(4, "El gráfico pie-update requiere exactamente 4 elementos"),
});

// ============================================
// SCHEMA UNIFICADO
// ============================================
export const ChartRequestSchema = z.discriminatedUnion("type", [
  BarChartRequestSchema,
  PieChartRequestSchema,
  RadialChartRequestSchema,
  BarReferralChartRequestSchema,
  PieOnlyColorChartRequestSchema,
  PieUpdateChartRequestSchema,
]);

// ============================================
// TIPOS TYPESCRIPT INFERIDOS
// ============================================
export type BarChartRequest = z.infer<typeof BarChartRequestSchema>;
export type PieChartRequest = z.infer<typeof PieChartRequestSchema>;
export type RadialChartRequest = z.infer<typeof RadialChartRequestSchema>;
export type BarReferralChartRequest = z.infer<typeof BarReferralChartRequestSchema>;
export type PieOnlyColorChartRequest = z.infer<typeof PieOnlyColorChartRequestSchema>;
export type PieUpdateChartRequest = z.infer<typeof PieUpdateChartRequestSchema>;
export type ChartRequest = z.infer<typeof ChartRequestSchema>;

// ============================================
// FUNCIONES DE VALIDACIÓN
// ============================================

/**
 * Valida un request de gráfico y retorna el resultado
 * @param chartData - Datos del gráfico a validar
 * @returns Objeto con success y data/error
 */
export function validateChartRequest(chartData: unknown) {
  const result = ChartRequestSchema.safeParse(chartData);

  if (!result.success) {
    const errors = result.error.errors.map(err => ({
      path: err.path.join('.'),
      message: err.message,
    }));

    return {
      success: false as const,
      errors,
      message: `Validación fallida: ${errors.map(e => `${e.path}: ${e.message}`).join(', ')}`,
    };
  }

  return {
    success: true as const,
    data: result.data,
  };
}

/**
 * Valida y lanza error si la validación falla
 * @param chartData - Datos del gráfico a validar
 * @returns Los datos validados
 * @throws Error si la validación falla
 */
export function validateChartRequestOrThrow(chartData: unknown): ChartRequest {
  try {
    return ChartRequestSchema.parse(chartData);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.map(err => `${err.path.join('.')}: ${err.message}`).join('\n');
      throw new Error(`Validación de gráfico fallida:\n${errors}`);
    }
    throw error;
  }
}

/**
 * Función helper para formatear errores de validación de forma amigable
 */
export function formatValidationErrors(errors: z.ZodError): string {
  return errors.errors
    .map(err => {
      const path = err.path.length > 0 ? `${err.path.join('.')}: ` : '';
      return `- ${path}${err.message}`;
    })
    .join('\n');
}
